import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import { Suspense, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { useIntl } from 'react-intl'
import type { BlueprintNode } from 'src/types/queries'
import type { CellType } from 'src/types/tableDataSet'
import { ColumnValuesDatalist } from '../../../../../components/ColumnValuesDatalist/ ColumnValuesDatalist'

type PivotValue = string | number | boolean

interface PivotValuesInputProps {
  blueprint: BlueprintNode
  column: string
  uiType: CellType | undefined
  values: PivotValue[]
  onChange: (values: PivotValue[]) => void
}

const coerce = (
  raw: string,
  uiType: CellType | undefined
): PivotValue | null => {
  const t = raw.trim()
  if (t === '') return null
  if (uiType === 'number') {
    const n = Number(t)
    return Number.isNaN(n) ? null : n
  }
  if (uiType === 'bool') {
    if (t === 'true') return true
    if (t === 'false') return false
    return null
  }
  return t
}

export const PivotValuesInput = ({
  blueprint,
  column,
  uiType,
  values,
  onChange
}: PivotValuesInputProps) => {
  const intl = useIntl()
  const [text, setText] = useState('')
  const disabled = column === ''
  const listId = `pivot-values-${column}`

  const addValue = () => {
    const v = coerce(text, uiType)
    setText('')
    if (v === null || values.includes(v)) return
    onChange([...values, v])
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addValue()
    }
  }

  return (
    <div className="pivot-values">
      <ul className="value-chips">
        {values.map((v) => (
          <li key={String(v)} className="chip">
            <span>{String(v)}</span>
            <button
              className="remove-chip"
              type="button"
              onClick={() => onChange(values.filter((x) => x !== v))}
            >
              <span className="icon icon-bin" />
            </button>
          </li>
        ))}
      </ul>

      <div className="value-add">
        <input
          type="text"
          list={listId}
          value={text}
          disabled={disabled}
          placeholder={intl.formatMessage({
            id: 'pivot-editor.value-placeholder'
          })}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          className="add-chip"
          type="button"
          onClick={addValue}
          disabled={disabled || text.trim() === ''}
        >
          <span className="icon icon-plus" />
        </button>

        {!disabled && (
          <ErrorBoundary errorComponent={() => null}>
            <Suspense fallback={null}>
              <ColumnValuesDatalist
                blueprint={blueprint}
                column={column}
                id={listId}
                filter={text}
                exclude={values.map(String)}
              />
            </Suspense>
          </ErrorBoundary>
        )}
      </div>
    </div>
  )
}
